import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, 
  Users, 
  Building2, 
  FileText, 
  Bell,
  Key,
  Database,
  Mail,
  Edit,
  Trash2,
  Plus,
  X
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user: authUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  
  // States for user management
  const [users, setUsers] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({});
  
  const [suppliers, setSuppliers] = useState([]);
  
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showSupplierDialog, setShowSupplierDialog] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    adresse: '',
    code_postal: '',
    ville: '',
    logo_url: '',
    upfront_percentage: 30,
    monthly_payment: 0,
    contract_duration: 12,
    margin_rate: 90,
    p0_margin: 90,
    payment_mode: 'upfront_monthly',
    is_active: true,
    p0_prices: {}  // Initialiser p0_prices comme objet vide
  });

  // States for email configuration
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Easy Power CRM',
    app_domain: 'crm.easy-power.fr',
    enabled: false
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // Charger les fournisseurs depuis l'API
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // D'abord, initialiser les fournisseurs si nécessaire
        await axios.post(`${API}/config/suppliers/initialize`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Puis récupérer la liste complète
        const response = await axios.get(`${API}/config/suppliers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setSuppliers(response.data);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        toast.error("Erreur lors du chargement des fournisseurs");
        // En cas d'erreur, garder un état vide pour éviter la confusion
        setSuppliers([]);
      }
    };

    fetchSuppliers();
  }, []);

  // Load email configuration
  useEffect(() => {
    const fetchEmailConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/config/email`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmailConfig(response.data);
      } catch (error) {
        console.error("Error fetching email config:", error);
        // Config not found or error - keep default state
      }
    };

    if (authUser && (authUser.role === 'admin' || authUser.is_super_admin)) {
      fetchEmailConfig();
    }
  }, [authUser]);

  // Load signature configuration
  useEffect(() => {
    const fetchSignatureConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/config/signature`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(prev => ({
          ...prev,
          signature: {
            ...prev.signature,
            workflow_url: response.data.workflow_url || ''
          }
        }));
      } catch (error) {
        console.error("Error fetching signature config:", error);
        // Config not found or error - keep default state
      }
    };

    if (authUser) {
      fetchSignatureConfig();
    }
  }, [authUser]);

  const [settings, setSettings] = useState({
    company: {
      name: "Easy Power",
      siret: "",
      address: "",
      phone: "",
      email: "contact@easypower.com"
    },
    notifications: {
      email_alerts: true,
      sms_alerts: false,
      contract_expiry_days: [60, 30, 15, 7],
      daily_digest: true
    },
    signature: {
      yousign_api_key: "",
      docusign_api_key: "",
      default_provider: "yousign"
    },
    suppliers: {
      auto_update_rates: true,
      margin_percentage: 8.0,
      commission_rate: 7.5
    }
  });

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    // S'assurer que p0_prices existe toujours comme objet
    setSupplierFormData({
      ...supplier,
      p0_prices: supplier.p0_prices || {}
    });
    setShowSupplierDialog(true);
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce fournisseur ?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/config/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedSuppliers = suppliers.filter(s => s.id !== supplierId);
      setSuppliers(updatedSuppliers);
      toast.success("Fournisseur supprimé avec succès");
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleSupplierStatus = async (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    const updatedSupplier = { ...supplier, is_active: !supplier.is_active };
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/config/suppliers/${supplierId}`, updatedSupplier, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedSuppliers = suppliers.map(s => 
        s.id === supplierId ? updatedSupplier : s
      );
      setSuppliers(updatedSuppliers);
      toast.success(`Fournisseur ${updatedSupplier.is_active ? 'activé' : 'désactivé'} avec succès`);
    } catch (error) {
      console.error("Error updating supplier status:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleSaveSupplier = async () => {
    // Validate required fields
    if (!supplierFormData.name) {
      toast.error("Veuillez remplir le nom du fournisseur");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingSupplier) {
        // Update existing supplier
        await axios.put(`${API}/config/suppliers/${editingSupplier.id}`, supplierFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const updatedSuppliers = suppliers.map(s => 
          s.id === editingSupplier.id ? { ...s, ...supplierFormData } : s
        );
        setSuppliers(updatedSuppliers);
        toast.success("Fournisseur modifié avec succès");
      } else {
        // Create new supplier
        const newSupplier = {
          ...supplierFormData,
          id: String(Math.max(...suppliers.map(s => parseInt(s.id) || 0)) + 1),
        };
        
        await axios.post(`${API}/config/suppliers`, newSupplier, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setSuppliers([...suppliers, newSupplier]);
        toast.success("Fournisseur ajouté avec succès");
      }
      
      // Reset form and close dialog
      setEditingSupplier(null);
      setSupplierFormData({
        name: '',
        adresse: '',
        code_postal: '',
        ville: '',
        logo_url: '',
        upfront_percentage: 30,
        monthly_payment: 0,
        contract_duration: 12,
        margin_rate: 90,
        p0_margin: 90,
        payment_mode: 'upfront_monthly',
        is_active: true,
        p0_prices: {}
      });
      setShowSupplierDialog(false);
    } catch (error) {
      console.error("Error saving supplier:", error);
      
      // Handle different error formats
      let errorMessage = "Erreur lors de la sauvegarde";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'Champ';
            return `${field}: ${err.msg}`;
          }).join(', ');
        }
        // Handle simple string error
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle object error
        else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Email configuration functions
  const handleSaveEmailConfig = async () => {
    setEmailConfigLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/config/email`, emailConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuration email sauvegardée avec succès!");
    } catch (error) {
      console.error("Error saving email config:", error);
      toast.error("Erreur lors de la sauvegarde de la configuration");
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/config/email/test`, {
        test_email: authUser.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email de test envoyé à ${authUser.email}`);
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du test");
    } finally {
      setTestingEmail(false);
    }
  };

  const handleEmailConfigChange = (field, value) => {
    setEmailConfig(prev => ({ ...prev, [field]: value }));
  };

  // User management functions
  const handleEditUser = (user) => {
    // Vérifier si un Admin essaie de modifier le Super Admin
    if (user.is_super_admin && !authUser?.is_super_admin) {
      toast.error("Seul le Super Admin peut modifier son propre compte");
      return;
    }
    setEditingUser(user);
    setUserFormData(user);
    setShowUserDialog(true);
  };

  const handleDeleteUser = async (userId) => {
    // Check if user is super admin
    const userToDelete = users.find(u => u.id === userId);
    if (userToDelete?.is_super_admin) {
      toast.error("Impossible de supprimer le compte Super Admin. Ce compte est protégé.");
      return;
    }
    
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success("Utilisateur supprimé avec succès");
    } catch (error) {
      console.error("Error deleting user:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const handleSaveUser = async () => {
    if (!userFormData.email || !userFormData.first_name || !userFormData.last_name) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    // Vérifier si l'utilisateur peut modifier les mots de passe
    const canModifyPassword = authUser?.role === 'admin' || authUser?.is_super_admin;

    try {
      if (editingUser) {
        // Update existing user
        const dataToUpdate = {...userFormData};
        
        // Si un mot de passe est fourni mais que l'utilisateur n'a pas les droits
        if (dataToUpdate.password && !canModifyPassword) {
          toast.error("Seuls les Administrateurs peuvent modifier les mots de passe");
          return;
        }
        
        // Si le mot de passe est vide lors de la modification, ne pas l'envoyer
        if (!dataToUpdate.password || dataToUpdate.password.trim() === '') {
          delete dataToUpdate.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, dataToUpdate);
        setUsers(users.map(u => u.id === editingUser.id ? {...u, ...dataToUpdate} : u));
        toast.success("Utilisateur modifié avec succès");
      } else {
        // Create new user
        if (!userFormData.password) {
          toast.error("Veuillez définir un mot de passe");
          return;
        }
        if (!userFormData.role) {
          toast.error("Veuillez sélectionner un rôle");
          return;
        }
        
        const response = await axios.post(`${API}/users`, userFormData);
        
        // Recharger la liste complète des utilisateurs depuis le serveur
        const usersResponse = await axios.get(`${API}/users`);
        setUsers(usersResponse.data);
        
        toast.success("Utilisateur créé avec succès");
      }
      
      setShowUserDialog(false);
      setEditingUser(null);
      setUserFormData({});
    } catch (error) {
      console.error("Error saving user:", error);
      const errorMessage = error.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(errorMessage);
    }
  };

  // Load users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`);
        setUsers(response.data);
      } catch (error) {
        console.error("Error loading users:", error);
      }
    };
    loadUsers();
  }, []);

  const handleSave = async (section) => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/${section}`, settings[section]);
      toast.success("Paramètres sauvegardés avec succès");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
          <p className="text-gray-600 mt-1">Configuration du système CRM Easy Power</p>
        </div>
        <div className="flex items-center space-x-2 text-blue-600">
          <Settings className="w-6 h-6" />
          <span className="font-medium">Administration</span>
        </div>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="company">Entreprise</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="signature">Signature Électronique</TabsTrigger>
          <TabsTrigger value="suppliers">Fournisseurs</TabsTrigger>
          <TabsTrigger value="suppliers-config">Config Fournisseurs</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="users">Utilisateurs</TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="w-5 h-5" />
                <span>Informations de l'Entreprise</span>
              </CardTitle>
              <CardDescription>
                Configuration des informations de base de votre société
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company-name">Nom de la société</Label>
                  <Input
                    id="company-name"
                    value={settings.company.name}
                    onChange={(e) => setSettings({
                      ...settings,
                      company: { ...settings.company, name: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="company-siret">SIRET</Label>
                  <Input
                    id="company-siret"
                    value={settings.company.siret}
                    onChange={(e) => setSettings({
                      ...settings,
                      company: { ...settings.company, siret: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="company-phone">Téléphone</Label>
                  <Input
                    id="company-phone"
                    value={settings.company.phone}
                    onChange={(e) => setSettings({
                      ...settings,
                      company: { ...settings.company, phone: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="company-email">Email</Label>
                  <Input
                    id="company-email"
                    type="email"
                    value={settings.company.email}
                    onChange={(e) => setSettings({
                      ...settings,
                      company: { ...settings.company, email: e.target.value }
                    })}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="company-address">Adresse complète</Label>
                <Textarea
                  id="company-address"
                  rows={3}
                  value={settings.company.address}
                  onChange={(e) => setSettings({
                    ...settings,
                    company: { ...settings.company, address: e.target.value }
                  })}
                />
              </div>

              <Button onClick={() => handleSave('company')} disabled={loading}>
                Sauvegarder les informations
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Settings */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="w-5 h-5" />
                <span>Système de Notifications</span>
              </CardTitle>
              <CardDescription>
                Configuration des alertes et notifications automatiques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Configuration Info - Redirect to Email Tab */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Configuration Email SMTP</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      La configuration du serveur email (SMTP) se trouve maintenant dans l'onglet <strong>"Email"</strong>.
                      Cliquez sur l'onglet Email ci-dessus pour configurer vos paramètres d'envoi d'emails.
                    </p>
                  </div>
                </div>
              </div>

              {/* Alert Types */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Types d'Alertes</h4>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label>Alertes d'échéances contractuelles</Label>
                      <p className="text-sm text-gray-500">Notifications automatiques avant expiration des contrats</p>
                    </div>
                    <Switch
                      checked={settings.notifications.contract_expiry_alerts || true}
                      onCheckedChange={(checked) => setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, contract_expiry_alerts: checked }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label>Alertes de commissions dues</Label>
                      <p className="text-sm text-gray-500">Notifications pour les paiements de commissions</p>
                    </div>
                    <Switch
                      checked={settings.notifications.commission_alerts || true}
                      onCheckedChange={(checked) => setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, commission_alerts: checked }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label>Alertes de nouveaux clients</Label>
                      <p className="text-sm text-gray-500">Notifications lors de l'ajout de nouveaux prospects/clients</p>
                    </div>
                    <Switch
                      checked={settings.notifications.new_client_alerts || false}
                      onCheckedChange={(checked) => setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, new_client_alerts: checked }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label>Rapports de trésorerie mensuels</Label>
                      <p className="text-sm text-gray-500">Envoi automatique du rapport mensuel par email</p>
                    </div>
                    <Switch
                      checked={settings.notifications.monthly_treasury_report || false}
                      onCheckedChange={(checked) => setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, monthly_treasury_report: checked }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Email Templates */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Modèles d'Email</h4>
                
                <div>
                  <Label htmlFor="contract_expiry_template">Modèle - Échéance contrat</Label>
                  <Textarea
                    id="contract_expiry_template"
                    rows={4}
                    placeholder="Bonjour {client_name}, votre contrat expire le {expiry_date}..."
                    value={settings.notifications.contract_expiry_template || ""}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, contract_expiry_template: e.target.value }
                    })}
                  />
                </div>

                <div>
                  <Label htmlFor="commission_reminder_template">Modèle - Rappel commission</Label>
                  <Textarea
                    id="commission_reminder_template"
                    rows={4}
                    placeholder="Bonjour {commercial_name}, une commission de {amount}€ est due..."
                    value={settings.notifications.commission_reminder_template || ""}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, commission_reminder_template: e.target.value }
                    })}
                  />
                </div>
              </div>

              {/* Legacy Settings */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Alertes par email</Label>
                    <p className="text-sm text-gray-500">Recevoir les alertes d'échéances par email</p>
                  </div>
                  <Switch
                    checked={settings.notifications.email_alerts}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, email_alerts: checked }
                    })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Alertes SMS</Label>
                    <p className="text-sm text-gray-500">Recevoir les alertes urgentes par SMS</p>
                  </div>
                  <Switch
                    checked={settings.notifications.sms_alerts}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, sms_alerts: checked }
                    })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Digest quotidien</Label>
                    <p className="text-sm text-gray-500">Résumé quotidien des activités</p>
                  </div>
                  <Switch
                    checked={settings.notifications.daily_digest}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, daily_digest: checked }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Délais d'alerte (jours avant échéance)</Label>
                <div className="grid grid-cols-4 gap-4 mt-2">
                  {settings.notifications.contract_expiry_days.map((days, index) => (
                    <Input
                      key={index}
                      type="number"
                      value={days}
                      onChange={(e) => {
                        const newDays = [...settings.notifications.contract_expiry_days];
                        newDays[index] = parseInt(e.target.value);
                        setSettings({
                          ...settings,
                          notifications: { ...settings.notifications, contract_expiry_days: newDays }
                        });
                      }}
                    />
                  ))}
                </div>
              </div>

              <Button onClick={() => handleSave('notifications')} disabled={loading}>
                Sauvegarder les notifications
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Signature Settings */}
        <TabsContent value="signature">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Signature Électronique</span>
              </CardTitle>
              <CardDescription>
                Configuration des intégrations Yousign et DocuSign
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="yousign-key">Clé API Yousign</Label>
                  <Input
                    id="yousign-key"
                    type="password"
                    placeholder="Saisissez votre clé API Yousign"
                    value={settings.signature.yousign_api_key}
                    onChange={(e) => setSettings({
                      ...settings,
                      signature: { ...settings.signature, yousign_api_key: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="docusign-key">Clé API DocuSign</Label>
                  <Input
                    id="docusign-key"
                    type="password"
                    placeholder="Saisissez votre clé API DocuSign"
                    value={settings.signature.docusign_api_key}
                    onChange={(e) => setSettings({
                      ...settings,
                      signature: { ...settings.signature, docusign_api_key: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Fournisseur par défaut</Label>
                <div className="flex space-x-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="signature_provider"
                      value="yousign"
                      checked={settings.signature.default_provider === "yousign"}
                      onChange={(e) => setSettings({
                        ...settings,
                        signature: { ...settings.signature, default_provider: e.target.value }
                      })}
                    />
                    <span>Yousign</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="signature_provider"
                      value="docusign"
                      checked={settings.signature.default_provider === "docusign"}
                      onChange={(e) => setSettings({
                        ...settings,
                        signature: { ...settings.signature, default_provider: e.target.value }
                      })}
                    />
                    <span>DocuSign</span>
                  </label>
                </div>
              </div>

              {/* NOUVEAU: Lien Workflow ACD */}
              <div className="border-t pt-6">
                <Label htmlFor="workflow-url">Lien de Signature Workflow ACD</Label>
                <p className="text-sm text-gray-500 mb-2">
                  URL du formulaire Yousign pour la signature du mandat ACD
                </p>
                <Input
                  id="workflow-url"
                  type="url"
                  placeholder="https://yousign.app/workflows/forms/..."
                  value={settings.signature.workflow_url || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    signature: { ...settings.signature, workflow_url: e.target.value }
                  })}
                  className="font-mono text-sm"
                />
              </div>

              <Button onClick={() => handleSave('signature')} disabled={loading}>
                Sauvegarder les paramètres de signature
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Settings */}
        <TabsContent value="suppliers">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="w-5 h-5" />
                <span>Configuration Fournisseurs</span>
              </CardTitle>
              <CardDescription>
                Gestion des tarifs et marges par défaut
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Mise à jour automatique des tarifs</Label>
                  <p className="text-sm text-gray-500">Synchroniser les prix fournisseurs chaque semaine</p>
                </div>
                <Switch
                  checked={settings.suppliers.auto_update_rates}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    suppliers: { ...settings.suppliers, auto_update_rates: checked }
                  })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="margin-p0">Marge par défaut P0 (€/Mgw)</Label>
                  <Input
                    id="margin-p0"
                    type="number"
                    step="0.01"
                    placeholder="90.00"
                    value={settings.suppliers.margin_p0 || 90}
                    onChange={(e) => setSettings({
                      ...settings,
                      suppliers: { ...settings.suppliers, margin_p0: parseFloat(e.target.value) }
                    })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Marge par défaut à 90€/Mgw</p>
                </div>
                <div>
                  <Label htmlFor="commission-marge">Marge (€/Mgw)</Label>
                  <Input
                    id="commission-marge"
                    type="number"
                    step="0.01"
                    placeholder="90.00"
                    value={settings.suppliers.commission_marge || 90}
                    onChange={(e) => setSettings({
                      ...settings,
                      suppliers: { ...settings.suppliers, commission_marge: parseFloat(e.target.value) }
                    })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Marge en euros par mégawatt</p>
                </div>
              </div>

              <Button onClick={() => handleSave('suppliers')} disabled={loading}>
                Sauvegarder les paramètres fournisseurs
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Configuration */}
        <TabsContent value="suppliers-config">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="w-5 h-5" />
                <span>Configuration des Fournisseurs</span>
              </CardTitle>
              <CardDescription>
                Gestion complète des fournisseurs d'énergie et de leurs paramètres
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {suppliers.map((supplier) => (
                  <div key={supplier.id} className={`p-4 border rounded-lg ${supplier.is_active ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-white border rounded-lg flex items-center justify-center">
                          {supplier.logo_url ? (
                            <img src={supplier.logo_url} alt={supplier.name} className="w-8 h-8 object-contain"/>
                          ) : (
                            <Building2 className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{supplier.name}</h4>
                          <div className="text-sm text-gray-600 grid grid-cols-2 gap-x-4">
                            <span>Avance: {supplier.upfront_percentage}%</span>
                            <span>Mensuel: {supplier.monthly_payment}€</span>
                            <span>Durée: {supplier.contract_duration} mois</span>
                            <span>Marge P0: {supplier.p0_margin}€/mWh</span>
                            <span>Commission: {supplier.margin_rate}€/Mgw</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={supplier.is_active}
                          onCheckedChange={() => handleToggleSupplierStatus(supplier.id)}
                        />
                        <span className="text-sm text-gray-600">
                          {supplier.is_active ? 'Actif' : 'Inactif'}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditSupplier(supplier)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteSupplier(supplier.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    setEditingSupplier(null);
                    setSupplierFormData({
                      name: '',
                      logo_url: '',
                      upfront_percentage: 30,
                      monthly_payment: 0,
                      contract_duration: 12,
                      margin_rate: 90, // Valeur par défaut en €/Mgw
                      p0_margin: 90, // Ancien champ (rétrocompat)
                      p0_prices: { // Nouveaux champs P0 multiples
                        base: 90,
                        hp: 95,
                        hc: 75,
                        hph: 100,
                        hch: 80,
                        hpe: 95,
                        hce: 75,
                        pointe: 110,
                        gaz: 60
                      },
                      payment_mode: 'upfront_monthly',
                      is_active: true
                    });
                    setShowSupplierDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un nouveau fournisseur
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Management */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Gestion des Utilisateurs</span>
              </CardTitle>
              <CardDescription>
                Administration des comptes et permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Add User Button */}
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Utilisateurs du système</h3>
                  <Dialog open={showUserDialog} onOpenChange={(open) => {
                    setShowUserDialog(open);
                    if (open && !editingUser) {
                      // Initialiser avec un rôle par défaut pour les nouveaux utilisateurs
                      setUserFormData({ role: 'commercial' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un utilisateur
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="user_email">Email</Label>
                          <Input
                            id="user_email"
                            type="email"
                            placeholder="user@easy-power.fr"
                            value={userFormData.email || ''}
                            onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="user_first_name">Prénom</Label>
                          <Input
                            id="user_first_name"
                            value={userFormData.first_name || ''}
                            onChange={(e) => setUserFormData({...userFormData, first_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="user_last_name">Nom</Label>
                          <Input
                            id="user_last_name"
                            value={userFormData.last_name || ''}
                            onChange={(e) => setUserFormData({...userFormData, last_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="user_role">Rôle</Label>
                          <Select 
                            value={userFormData.role || 'commercial'} 
                            onValueChange={(value) => setUserFormData({...userFormData, role: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Administrateur</SelectItem>
                              <SelectItem value="commercial">Commercial</SelectItem>
                              <SelectItem value="regie">Régie</SelectItem>
                              <SelectItem value="administratif">Administratif</SelectItem>
                              <SelectItem value="telepro">Telepro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {(() => {
                          // Logique d'affichage du champ mot de passe
                          const isEditingSuperAdmin = editingUser?.is_super_admin;
                          const isSuperAdminUser = authUser?.is_super_admin;
                          const isEditingSelf = editingUser?.id === authUser?.id;
                          
                          // Cas 1: Création d'utilisateur (admin ou super admin)
                          if (!editingUser && (authUser?.role === 'admin' || isSuperAdminUser)) {
                            return (
                              <div>
                                <Label htmlFor="user_password">Mot de passe</Label>
                                <Input
                                  id="user_password"
                                  type="password"
                                  placeholder="Mot de passe temporaire"
                                  value={userFormData.password || ''}
                                  onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                                />
                              </div>
                            );
                          }
                          
                          // Cas 2: Modification d'un utilisateur Super Admin (seul lui-même peut)
                          if (isEditingSuperAdmin && isSuperAdminUser && isEditingSelf) {
                            return (
                              <div>
                                <Label htmlFor="user_password">
                                  Nouveau mot de passe (laisser vide pour ne pas changer)
                                </Label>
                                <Input
                                  id="user_password"
                                  type="password"
                                  placeholder="Laisser vide pour conserver l'ancien"
                                  value={userFormData.password || ''}
                                  onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                                />
                              </div>
                            );
                          }
                          
                          // Cas 3: Modification d'un utilisateur normal (admin ou super admin peut)
                          if (editingUser && !isEditingSuperAdmin && (authUser?.role === 'admin' || isSuperAdminUser)) {
                            return (
                              <div>
                                <Label htmlFor="user_password">
                                  Nouveau mot de passe (laisser vide pour ne pas changer)
                                </Label>
                                <Input
                                  id="user_password"
                                  type="password"
                                  placeholder="Laisser vide pour conserver l'ancien"
                                  value={userFormData.password || ''}
                                  onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                                />
                              </div>
                            );
                          }
                          
                          // Sinon, ne pas afficher le champ
                          return null;
                        })()}
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" onClick={() => setShowUserDialog(false)}>
                            Annuler
                          </Button>
                          <Button onClick={handleSaveUser}>
                            {editingUser ? 'Modifier' : 'Créer'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Users List */}
                <div className="space-y-4">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{user.first_name} {user.last_name}</h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge className={
                              user.role === 'admin' ? 'bg-red-100 text-red-800' :
                              user.role === 'commercial' ? 'bg-green-100 text-green-800' :
                              user.role === 'regie' ? 'bg-blue-100 text-blue-800' :
                              user.role === 'telepro' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {user.role === 'admin' ? 'Administrateur' :
                               user.role === 'commercial' ? 'Commercial' :
                               user.role === 'regie' ? 'Régie' :
                               user.role === 'telepro' ? 'Telepro' :
                               'Administratif'}
                            </Badge>
                            {user.is_super_admin && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                                🔒 SUPER ADMIN
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          disabled={user.is_super_admin && !authUser?.is_super_admin}
                          title={user.is_super_admin && !authUser?.is_super_admin ? "Seul le Super Admin peut modifier ce compte" : "Modifier l'utilisateur"}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-700"
                          disabled={user.is_super_admin}
                          title={user.is_super_admin ? "Le Super Admin ne peut pas être supprimé" : "Supprimer l'utilisateur"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Configuration Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Configuration Email
              </CardTitle>
              <CardDescription>
                Configurez les paramètres SMTP pour l'envoi d'emails (réinitialisation de mot de passe, notifications, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Enable/Disable Email */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">Activer l'envoi d'emails</h3>
                    <p className="text-sm text-gray-600">Activer ou désactiver la fonctionnalité d'envoi d'emails</p>
                  </div>
                  <Switch
                    checked={emailConfig.enabled}
                    onCheckedChange={(checked) => handleEmailConfigChange('enabled', checked)}
                  />
                </div>

                {/* SMTP Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp_host">Serveur SMTP</Label>
                    <Input
                      id="smtp_host"
                      value={emailConfig.smtp_host}
                      onChange={(e) => handleEmailConfigChange('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="smtp_port">Port SMTP</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={emailConfig.smtp_port}
                      onChange={(e) => handleEmailConfigChange('smtp_port', parseInt(e.target.value))}
                      placeholder="587"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="smtp_user">Email utilisateur</Label>
                    <Input
                      id="smtp_user"
                      type="email"
                      value={emailConfig.smtp_user}
                      onChange={(e) => handleEmailConfigChange('smtp_user', e.target.value)}
                      placeholder="noreply@exemple.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="smtp_password">Mot de passe SMTP</Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      value={emailConfig.smtp_password}
                      onChange={(e) => handleEmailConfigChange('smtp_password', e.target.value)}
                      placeholder="Mot de passe d'application"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="smtp_from_email">Email expéditeur</Label>
                    <Input
                      id="smtp_from_email"
                      type="email"
                      value={emailConfig.smtp_from_email}
                      onChange={(e) => handleEmailConfigChange('smtp_from_email', e.target.value)}
                      placeholder="noreply@exemple.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="smtp_from_name">Nom expéditeur</Label>
                    <Input
                      id="smtp_from_name"
                      value={emailConfig.smtp_from_name}
                      onChange={(e) => handleEmailConfigChange('smtp_from_name', e.target.value)}
                      placeholder="Easy Power CRM"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="app_domain">Domaine de l'application</Label>
                    <Input
                      id="app_domain"
                      value={emailConfig.app_domain}
                      onChange={(e) => handleEmailConfigChange('app_domain', e.target.value)}
                      placeholder="crm.easy-power.fr"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Utilisé pour générer les liens de réinitialisation de mot de passe
                    </p>
                  </div>
                </div>

                {/* Information Box */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">📧 Configuration Gmail</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Serveur SMTP: smtp.gmail.com</li>
                    <li>• Port: 587</li>
                    <li>• Activez la validation en 2 étapes sur votre compte Gmail</li>
                    <li>• Créez un "Mot de passe d'application" dans les paramètres Gmail</li>
                    <li>• Utilisez ce mot de passe d'application (pas votre mot de passe Gmail normal)</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={handleTestEmail}
                    disabled={testingEmail || !emailConfig.enabled}
                  >
                    {testingEmail ? "Envoi en cours..." : "Envoyer un email de test"}
                  </Button>
                  <Button 
                    onClick={handleSaveEmailConfig}
                    disabled={emailConfigLoading}
                  >
                    {emailConfigLoading ? "Sauvegarde..." : "Sauvegarder la configuration"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Supplier Edit Dialog */}
      <Dialog open={showSupplierDialog} onOpenChange={setShowSupplierDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Fournisseur</DialogTitle>
            <DialogDescription>
              Configurez les paramètres du fournisseur d'énergie
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="supplier_name">Nom du fournisseur</Label>
                  <Input
                    id="supplier_name"
                    value={supplierFormData.name || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="supplier_adresse">Adresse</Label>
                  <Input
                    id="supplier_adresse"
                    value={supplierFormData.adresse || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, adresse: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="supplier_code_postal">Code postal</Label>
                  <Input
                    id="supplier_code_postal"
                    value={supplierFormData.code_postal || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, code_postal: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="supplier_ville">Ville</Label>
                  <Input
                    id="supplier_ville"
                    value={supplierFormData.ville || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, ville: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="logo_upload">Logo du fournisseur</Label>
                  <div className="space-y-2">
                    {/* Current logo preview */}
                    {supplierFormData.logo_url && (
                      <div className="flex items-center space-x-2 p-2 border rounded">
                        <img 
                          src={supplierFormData.logo_url} 
                          alt="Logo actuel" 
                          className="w-12 h-12 object-contain border rounded"
                        />
                        <span className="text-sm text-gray-600">Logo actuel</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSupplierFormData({...supplierFormData, logo_url: ''})}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    
                    {/* File upload */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        id="logo_upload"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Create preview URL
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              setSupplierFormData({...supplierFormData, logo_url: e.target.result});
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <label htmlFor="logo_upload" className="cursor-pointer">
                        <div className="space-y-2">
                          <Building2 className="w-8 h-8 mx-auto text-gray-400" />
                          <div className="text-sm text-gray-600">
                            <span className="text-blue-600 hover:text-blue-700">Cliquez pour télécharger</span> ou glissez une image
                          </div>
                          <div className="text-xs text-gray-400">
                            PNG, JPG, SVG jusqu'à 2MB
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section Adresse */}
              <div>
                <Label className="text-lg font-semibold">Adresse du fournisseur</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <div className="md:col-span-3">
                    <Label htmlFor="supplier_adresse">Adresse</Label>
                    <Input
                      id="supplier_adresse"
                      value={supplierFormData.adresse || ''}
                      onChange={(e) => setSupplierFormData({...supplierFormData, adresse: e.target.value})}
                      placeholder="2, rue Berthelot"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supplier_code_postal">Code postal</Label>
                    <Input
                      id="supplier_code_postal"
                      value={supplierFormData.code_postal || ''}
                      onChange={(e) => setSupplierFormData({...supplierFormData, code_postal: e.target.value})}
                      placeholder="92400"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="supplier_ville">Ville</Label>
                    <Input
                      id="supplier_ville"
                      value={supplierFormData.ville || ''}
                      onChange={(e) => setSupplierFormData({...supplierFormData, ville: e.target.value})}
                      placeholder="Courbevoie"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="upfront_percentage">Paiement d'avance (%)</Label>
                  <Input
                    id="upfront_percentage"
                    type="number"
                    min="0"
                    max="100"
                    value={supplierFormData.upfront_percentage || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, upfront_percentage: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label htmlFor="monthly_payment">Paiement mensuel (€)</Label>
                  <Input
                    id="monthly_payment"
                    type="number"
                    min="0"
                    value={supplierFormData.monthly_payment || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, monthly_payment: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label htmlFor="contract_duration">Durée du contrat (mois)</Label>
                  <Input
                    id="contract_duration"
                    type="number"
                    min="1"
                    value={supplierFormData.contract_duration || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, contract_duration: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label htmlFor="margin_rate">Taux de commission (€/Mgw)</Label>
                  <Input
                    id="margin_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 90.00"
                    value={supplierFormData.margin_rate || ''}
                    onChange={(e) => setSupplierFormData({...supplierFormData, margin_rate: parseFloat(e.target.value)})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Commission en euros par mégawatt
                  </p>
                </div>
                
                {/* Section P0 multiples par segment */}
                <div className="col-span-2 border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Prix P0 par Segment (€/MWh)</h4>
                  <p className="text-xs text-gray-500 mb-4">
                    Configurez les prix de base P0 pour chaque segment. Ces valeurs seront utilisées dans le simulateur.
                  </p>
                  
                  {/* C5 BASE */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <h5 className="font-medium text-sm text-gray-700 mb-2">C5 BASE</h5>
                    <div>
                      <Label htmlFor="p0_base">Prix Base (€/MWh)</Label>
                      <Input
                        id="p0_base"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 90.00"
                        value={supplierFormData.p0_prices?.base || ''}
                        onChange={(e) => setSupplierFormData({
                          ...supplierFormData, 
                          p0_prices: {
                            ...(supplierFormData.p0_prices || {}),
                            base: parseFloat(e.target.value) || 0
                          }
                        })}
                      />
                    </div>
                  </div>
                  
                  {/* C5 HP/HC */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <h5 className="font-medium text-sm text-gray-700 mb-2">C5 HP/HC</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="p0_hp">Prix HP (€/MWh)</Label>
                        <Input
                          id="p0_hp"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 95.00"
                          value={supplierFormData.p0_prices?.hp || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hp: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_hc">Prix HC (€/MWh)</Label>
                        <Input
                          id="p0_hc"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 75.00"
                          value={supplierFormData.p0_prices?.hc || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hc: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* C4 */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <h5 className="font-medium text-sm text-gray-700 mb-2">C4</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="p0_hph">Prix HPH (€/MWh)</Label>
                        <Input
                          id="p0_hph"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 100.00"
                          value={supplierFormData.p0_prices?.hph || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hph: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_hch">Prix HCH (€/MWh)</Label>
                        <Input
                          id="p0_hch"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 80.00"
                          value={supplierFormData.p0_prices?.hch || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hch: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_hpe">Prix HPE (€/MWh)</Label>
                        <Input
                          id="p0_hpe"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 95.00"
                          value={supplierFormData.p0_prices?.hpe || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hpe: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_hce">Prix HCE (€/MWh)</Label>
                        <Input
                          id="p0_hce"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 75.00"
                          value={supplierFormData.p0_prices?.hce || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hce: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* C2 */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <h5 className="font-medium text-sm text-gray-700 mb-2">C2</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="p0_pointe">Prix POINTE (€/MWh)</Label>
                        <Input
                          id="p0_pointe"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 110.00"
                          value={supplierFormData.p0_prices?.pointe || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              pointe: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_c2_hph">Prix HPH (€/MWh)</Label>
                        <Input
                          id="p0_c2_hph"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 100.00"
                          value={supplierFormData.p0_prices?.hph || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hph: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_c2_hch">Prix HCH (€/MWh)</Label>
                        <Input
                          id="p0_c2_hch"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 80.00"
                          value={supplierFormData.p0_prices?.hch || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hch: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_c2_hpe">Prix HPE (€/MWh)</Label>
                        <Input
                          id="p0_c2_hpe"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 95.00"
                          value={supplierFormData.p0_prices?.hpe || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hpe: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="p0_c2_hce">Prix HCE (€/MWh)</Label>
                        <Input
                          id="p0_c2_hce"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 75.00"
                          value={supplierFormData.p0_prices?.hce || ''}
                          onChange={(e) => setSupplierFormData({
                            ...supplierFormData, 
                            p0_prices: {
                              ...(supplierFormData.p0_prices || {}),
                              hce: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* GAZ */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <h5 className="font-medium text-sm text-gray-700 mb-2">GAZ</h5>
                    <div>
                      <Label htmlFor="p0_gaz">Prix Gaz (€/MWh)</Label>
                      <Input
                        id="p0_gaz"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 60.00"
                        value={supplierFormData.p0_prices?.gaz || ''}
                        onChange={(e) => setSupplierFormData({
                          ...supplierFormData, 
                          p0_prices: {
                            ...(supplierFormData.p0_prices || {}),
                            gaz: parseFloat(e.target.value) || 0
                          }
                        })}
                      />
                    </div>
                  </div>
                </div>
                
                
                <div>
                  <Label htmlFor="payment_mode">Mode de paiement client</Label>
                  <Select 
                    value={supplierFormData.payment_mode || 'upfront_monthly'} 
                    onValueChange={(value) => setSupplierFormData({...supplierFormData, payment_mode: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upfront_monthly">Avance + Paiements mensuels</SelectItem>
                      <SelectItem value="full_upfront">100% en avance</SelectItem>
                      <SelectItem value="split_50_50">50% avance + 50% différé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {supplierFormData.payment_mode === "split_50_50" && (
                  <div>
                    <Label htmlFor="split_payment_delay">Délai 2ème paiement (mois)</Label>
                    <Input
                      id="split_payment_delay"
                      type="number"
                      min="1"
                      max="12"
                      placeholder="Ex: 3"
                      value={supplierFormData.split_payment_delay || ''}
                      onChange={(e) => setSupplierFormData({...supplierFormData, split_payment_delay: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Nombre de mois après le début du contrat pour le 2ème paiement
                    </p>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={supplierFormData.is_active || false}
                    onCheckedChange={(checked) => setSupplierFormData({...supplierFormData, is_active: checked})}
                  />
                  <Label htmlFor="is_active">Fournisseur actif</Label>
                </div>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Informations sur les paramètres</h4>
                <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                  <li><strong>Paiement d'avance :</strong> Pourcentage versé immédiatement</li>
                  <li><strong>Paiement mensuel :</strong> Montant récurrent pendant la durée</li>
                  <li><strong>Taux de commission :</strong> Marge en euros par mégawatt (€/Mgw)</li>
                  <li><strong>Marge P0 :</strong> Marge par défaut à 90€/mWh</li>
                </ul>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowSupplierDialog(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveSupplier}>
                  Enregistrer les modifications
                </Button>
              </div>
            </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;